/**
 * addGroupModal.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import { ObjectId } from "bson";
import * as React from "react";

import {
  Dialog as MDDialog,
  DialogContent,
  DialogTitle,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stepper,
  Step,
  StepButton,
  StepLabel,
  StepContent,
  TextField,
  Checkbox
} from "@mui/material";

import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { Membership } from "../models/auth";
import { RatchetTree } from "../models/crypto";
import {
  Group, GroupMembership, GroupSubmissionType, GroupType,
  GroupPKI, PROTOCOL_VERSION, ProtocolScheme
} from "../models/group";

interface AddGroupDialogProps {
  open: boolean,
  membership: Membership | null,
  onClose(): void,
  onFinishGroup(group: Group): Promise<[RatchetTree | null, GroupMembership | null]>
}

interface AddGroupDialogState {
  activeStep: number,
  groupname: string,
  desc: string,
  grouptype: GroupType,
  submissiontype: GroupSubmissionType,
  pki: GroupPKI,
  protocolScheme: ProtocolScheme,
  archiving: boolean,
  saving: boolean,
  errors: {
    groupname: string
  }
  errMsg: string
}

export class AddGroupDialog extends React.Component<AddGroupDialogProps, AddGroupDialogState> {

  constructor(props: AddGroupDialogProps) {
    super(props);
    this.finishAdding = this.finishAdding.bind(this);
    this.updateValue = this.updateValue.bind(this);

    this.state = {
      activeStep: 0,
      groupname: "",
      desc: "",
      grouptype: GroupType.public,
      submissiontype: GroupSubmissionType.normal,
      pki: GroupPKI.p256,
      protocolScheme: ProtocolScheme.mls10,
      archiving: false,
      saving: false,
      errors: {
        groupname: ""
      },
      errMsg: ""
    };
  }

  private updateValue(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const update: any = { [propName]: inputElem.value };
    this.setState(update);
  }

  private async finishAdding() {
    if (!this.state.groupname) {
      this.setState({
        errors: {
          ...this.state.errors,
          groupname: "Please, enter a group name"
        }
      });
      return;
    }
    if (!this.props.membership) {
      this.setState({ errMsg: "Missing Account Membership" });
      return;
    }
    this.props.membership.canArchive && !this.props.membership.mustArchive

    try {
      this.setState({ saving: true });
      console.log("saving group:", this.state);
      await this.props.onFinishGroup({
        _id: new ObjectId().toString(),
        accountID: "",
        groupname: this.state.groupname,
        desc: this.state.desc,
        grouptype: this.state.grouptype,
        submissiontype: this.state.submissiontype,
        protocolScheme: typeof this.state.protocolScheme === "string" ?
          parseInt(this.state.protocolScheme) :
          this.state.protocolScheme,
        pki: this.state.pki,
        epoch: 0,
        msgCounter: 0,
        archiving: this.props.membership.canArchive ?
          (this.props.membership.mustArchive ? true : this.state.archiving) : false,
        protoVersion: PROTOCOL_VERSION
      });
      this.setState({ groupname: "", desc: "", pki: GroupPKI.p256, activeStep: 0 });
    } catch (err) {
      console.error("Error saving group:", err);
      this.setState({ errMsg: (err as Error).message });
    } finally {
      this.setState({ saving: false });
    }
  }

  //<FormLabel component="legend">Visibility &amp; Access</FormLabel>
  //<FormLabel component="legend">Posting Behavior</FormLabel>

  //loading={this.state.saving}
  //onConfirm={this.finishAdding}
  render(): React.ReactElement<any, any> {
    return <MDDialog title="Add Group" className="add-group-modal"
      open={this.props.open}
      onClose={this.props.onClose}>
      <DialogTitle>Add Group</DialogTitle>
      <DialogContent>
        <Stepper nonLinear activeStep={this.state.activeStep} orientation="vertical">
          <Step completed={this.state.activeStep > 0 && this.state.groupname !== ""}>
            <StepButton onClick={() => this.setState({ activeStep: 0 })}>
              <StepLabel error={this.state.activeStep > 0 && !this.state.groupname}>
                Group Name &amp; Description
              </StepLabel>
            </StepButton>
            <StepContent>
              <form className="form-wrapper">
                <div className="form-row">
                  <TextField id="groupname" name="groupname" fullWidth required
                    label="Group Name" size="small"
                    variant="outlined" color="primary"
                    value={this.state.groupname} onChange={this.updateValue} />
                </div>
                <div className="form-row">
                  <TextField className="group-desc" id="desc" name="desc" fullWidth multiline
                    variant="outlined" label="Group Description" color="primary" size="small"
                    value={this.state.desc} onChange={this.updateValue} />
                </div>
                <div className="button-row">
                  <Button color="primary" variant="contained"
                    disableRipple disableTouchRipple
                    disabled={!this.state.groupname}
                    onClick={() => this.setState({ activeStep: 1 })}>
                    Next
                  </Button>
                </div>
              </form>
            </StepContent>
          </Step>
          <Step>
            <StepButton onClick={() => this.setState({ activeStep: 1 })}>
              <StepLabel>
                Visibility &amp; Access
              </StepLabel>
            </StepButton>
            <StepContent>
              <div className="form-row">
                <FormControl component="fieldset" className="group-type-selection">
                  <RadioGroup name="grouptype" color="primary"
                    value={this.state.grouptype}
                    onChange={this.updateValue}>
                    <FormLabel>
                      <Radio value={GroupType.public} name="grouptype" disableRipple />
                      <div className="radio-label-desc">
                        <dt>Open</dt>
                        <dd>Visible to members of this account. Members can join on their own.
                          Guests must be added.
                        </dd>
                      </div>
                    </FormLabel>
                    <FormLabel>
                      <Radio value={GroupType.private} name="grouptype" disableRipple />
                      <div className="radio-label-desc">
                        <dt>Private</dt>
                        <dd>Not shown to members of this account. Invite only.</dd>
                      </div>
                    </FormLabel>
                  </RadioGroup>
                </FormControl>
              </div>
              {this.props.membership && this.props.membership.canArchive && !this.props.membership.mustArchive &&
                <div className="form-row group-archiving-toggle">
                  <FormControl component="fieldset" className="group-archiving-toggle" fullWidth>
                    <FormLabel component="legend">Group Archiving</FormLabel>
                    <FormControlLabel control={
                      <Checkbox color="primary"
                        disableFocusRipple={true}
                        disableRipple={true}
                        disabled={!this.props.membership.canArchive || this.props.membership.mustArchive}
                        checked={this.state.archiving}
                        onChange={() => this.setState({ archiving: !this.state.archiving })}
                      />}
                      label="Archive Group Messages"
                    />
                  </FormControl>
                </div>
              }
              <div className="button-row">
                <Button
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({ activeStep: 0 })}>Back</Button>
                <Button color="primary" variant="contained"
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({ activeStep: 2 })}>
                  Next
                </Button>
              </div>
            </StepContent>
          </Step>
          <Step>
            <StepButton onClick={() => this.setState({ activeStep: 2 })}>
              <StepLabel>Posting Behavior</StepLabel>
            </StepButton>
            <StepContent>
              <FormControl component="fieldset" className="group-type-selection">
                <RadioGroup name="submissiontype" color="primary"
                  value={this.state.submissiontype}
                  onChange={this.updateValue}>
                  <FormLabel>
                    <Radio value={GroupSubmissionType.normal} name="submissiontype" disableRipple />
                    <div className="radio-label-desc">
                      <dt>Normal</dt>
                      <dd>Group Owners, Managers, and Users can read and submit messages.</dd>
                    </div>
                  </FormLabel>
                  <FormLabel>
                    <Radio value={GroupSubmissionType.broadcastOnly} name="submissiontype" disableRipple />
                    <div className="radio-label-desc">
                      <dt>Broadcast Only</dt>
                      <dd>Only group Owners and Managers can post. All group members can read.</dd>
                    </div>
                  </FormLabel>
                  <FormLabel>
                    <Radio value={GroupSubmissionType.dropbox} name="submissiontype" disableRipple />
                    <div className="radio-label-desc">
                      <dt>Submission</dt>
                      <dd>Group Owners, Managers, and Users can post messages.
                        Only Owners and Managers can read.</dd>
                    </div>
                  </FormLabel>
                </RadioGroup>
              </FormControl>
              <div className="button-row">
                <Button
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({ activeStep: 1 })}>Back</Button>
                <Button color="primary" variant="contained"
                  disabled={!this.state.groupname}
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({ activeStep: 3 })}>
                  Next
                </Button>
              </div>
            </StepContent>
          </Step>
          <Step>
            <StepButton onClick={() => this.setState({ activeStep: 3 })}>
              <StepLabel>Preserve Chat History</StepLabel>
            </StepButton>
            <StepContent>
              <FormControl component="fieldset" className="group-encryption-scheme-selection">
                <RadioGroup name="protocolScheme" color="primary"
                  value={this.state.protocolScheme}
                  onChange={this.updateValue}>
                  <FormLabel>
                    <Radio value={ProtocolScheme.mls10} name="protocolScheme" disableRipple />
                    <div className="radio-label-desc">
                      <dt>Disable</dt>
                      <dd>Messages are only stored locally on participating devices. Devices only
                        view the messages sent while they're participating in the group.
                      </dd>
                    </div>
                  </FormLabel>
                  <FormLabel>
                    <Radio value={ProtocolScheme.mlsSharedSecret} name="protocolScheme" disableRipple
                      color="error" />
                    <div className="radio-label-desc">
                      <dt>Enable</dt>
                      <dd>
                        <span>An encrypted copy of each message is stored with the server, so that newly
                          added devices may download, decrypt, and view the chat history from prior to
                          the point they joined the group.
                        </span>
                        <em className="warning"> While this maintains end-to-end encryption,
                          it severely weakens the security of the group!
                        </em>
                      </dd>
                    </div>
                  </FormLabel>
                </RadioGroup>
              </FormControl>
              <div className="button-row">
                <Button
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({ activeStep: 2 })}>Back</Button>
                <Button color="primary" variant="contained"
                  disabled={!this.state.groupname}
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({ activeStep: 4 })}>
                  Next
                </Button>
              </div>
            </StepContent>
          </Step>
          <Step>
            <StepButton onClick={() => this.setState({ activeStep: 4 })}>
              <StepLabel>Public Key Crypto Scheme</StepLabel>
            </StepButton>
            <StepContent>
              <FormControl component="fieldset" className="group-pki-selection">
                <RadioGroup name="pki" color="primary"
                  value={this.state.pki}
                  onChange={this.updateValue}>
                  <FormLabel>
                    <Radio value={GroupPKI.p256} name="pki" disableRipple />
                    <div className="radio-label-desc">
                      <dt>Elliptic Curve</dt>
                      <dd>Traditional, tried, and true public key encryption. Suitable for
                        most. Should maintain privacy for another 5-10 years.
                      </dd>
                    </div>
                  </FormLabel>
                  <FormLabel>
                    <Radio value={GroupPKI.crystals} name="pki" disableRipple />
                    <div className="radio-label-desc">
                      <dt>CRYSTALS</dt>
                      <dd>Experimental. Newly approved for privacy when faced with full scale
                        quantum computing. Should maintain privacy for 20-30 years.</dd>
                    </div>
                  </FormLabel>
                </RadioGroup>
              </FormControl>
              <div className="button-row">
                <Button
                  disableRipple disableTouchRipple
                  onClick={() => this.setState({ activeStep: 3 })}>Back</Button>
                <Button color="primary" variant="contained"
                  disabled={!this.state.groupname}
                  disableRipple disableTouchRipple
                  onClick={this.finishAdding}>
                  Finish
                </Button>
              </div>
            </StepContent>
          </Step>
        </Stepper>
        <ErrorSnackBar errMsg={this.state.errMsg} didClose={() => this.setState({ errMsg: "" })} />
      </DialogContent>
    </MDDialog>;
  }
}
